import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getRetirementGoalFieldExplainers } from '@/content/field-explainers/retirement-goal';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateRetirementGoal, type RetirementGoalInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  formatPercent,
  parseContractState
} from './shared';

type RetirementGoalState = {
  retirementGoal: number;
  currentSavings: number;
  currentAge: number;
  retirementAge: number;
  expectedReturn: number;
};

const defaults: RetirementGoalState = {
  retirementGoal: 1800000,
  currentSavings: 240000,
  currentAge: 34,
  retirementAge: 60,
  expectedReturn: 7
};

const chartConfig = {
  balance: {
    label: 'Projected balance',
    color: 'var(--chart-1)'
  },
  contributions: {
    label: 'Annual contributions',
    color: 'var(--chart-2)'
  },
  goalLine: {
    label: 'Goal line',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

type Props = {
  route: RouteDefinition;
};

function toEngineInput(state: RetirementGoalState): RetirementGoalInput {
  return {
    retirementGoal: state.retirementGoal,
    currentSavings: state.currentSavings,
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    expectedReturn: state.expectedReturn
  };
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function RetirementGoalCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<RetirementGoalState>(() => parseContractState('retirementGoal', defaults));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateRetirementGoal(toEngineInput(state)), [state]);
  const fundingRatio = state.retirementGoal > 0 ? (result.projectedFromCurrent / state.retirementGoal) * 100 : 0;
  const finalBalance = result.yearlyData.length > 0 ? result.yearlyData[result.yearlyData.length - 1].endBalance : state.currentSavings;
  const onTarget = Math.abs(result.shortfall) < 1;

  const explainers = useMemo(() => {
    const entries = getRetirementGoalFieldExplainers({
      input: {
        retirementGoal: state.retirementGoal,
        currentSavings: state.currentSavings,
        currentAge: state.currentAge,
        retirementAge: state.retirementAge,
        expectedReturn: state.expectedReturn
      },
      result: {
        requiredMonthlySavings: result.requiredMonthlySavings,
        projectedFromCurrent: result.projectedFromCurrent,
        yearsToRetirement: result.yearsToRetirement,
        finalBalance
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result, finalBalance]);

  const chartData = useMemo(
    () =>
      result.yearlyData.map((row, index) => ({
        year: row.year,
        age: row.age,
        balance: row.endBalance,
        contributions: row.contributions,
        goalLine: result.chart.goalLine[index]
      })),
    [result.chart.goalLine, result.yearlyData]
  );

  const shareState = {
    retirementGoal: state.retirementGoal,
    currentSavings: state.currentSavings,
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    expectedReturn: state.expectedReturn
  };

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Retirement Goal Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Work backward from your retirement target to estimate required monthly savings and milestone timing.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Retirement target assumptions"
          description="Set your goal, timeline, and expected return to model contribution requirements."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="goalTarget" label="Retirement target" value={state.retirementGoal} min={100000} step={10000} prefix="$" explainer={explainers.byId.get('retirementGoal')} onChange={(value) => setState((prev) => ({ ...prev, retirementGoal: value }))} />
            <NumberField id="goalCurrentSavings" label="Current savings" value={state.currentSavings} min={0} step={1000} prefix="$" explainer={explainers.byId.get('currentSavings')} onChange={(value) => setState((prev) => ({ ...prev, currentSavings: value }))} />
            <NumberField id="goalCurrentAge" label="Current age" value={state.currentAge} min={18} max={85} parseMode="integer" explainer={explainers.byId.get('currentAge')} onChange={(value) => setState((prev) => ({ ...prev, currentAge: value }))} />
            <NumberField id="goalRetirementAge" label="Retirement age" value={state.retirementAge} min={state.currentAge + 1} max={90} parseMode="integer" explainer={explainers.byId.get('retirementAge')} onChange={(value) => setState((prev) => ({ ...prev, retirementAge: value }))} />
            <NumberField id="goalExpectedReturn" label="Expected return" value={state.expectedReturn} min={0} max={15} step={0.1} suffix="%" explainer={explainers.byId.get('expectedReturn')} onChange={(value) => setState((prev) => ({ ...prev, expectedReturn: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Required monthly savings" value={formatCurrency(result.requiredMonthlySavings)} note="to meet target on schedule" trend={result.requiredMonthlySavings > 0 ? 'up' : 'flat'} explainer={explainers.byId.get('requiredMonthlySavings')} />
          <ResultSummaryCard label="Projected from current balance" value={formatCurrency(result.projectedFromCurrent)} note={`${formatPercent(fundingRatio)} of target without new savings`} trend={result.alreadyOnTrack ? 'up' : 'flat'} explainer={explainers.byId.get('projectedFromCurrent')} />
          <ResultSummaryCard
            label="Shortfall at retirement"
            value={onTarget ? '$0' : formatCurrency(Math.abs(result.shortfall))}
            note={onTarget ? 'On pace to land on target' : result.shortfall >= 0 ? 'Projected surplus against target' : 'Remaining gap at projection horizon'}
            trend={result.shortfall >= 0 || onTarget ? 'up' : 'down'}
            explainer={explainers.byId.get('shortfall')}
          />
          <ResultSummaryCard label="Years to retirement" value={`${result.yearsToRetirement} years`} note={`from age ${state.currentAge} to ${state.retirementAge}`} trend="flat" explainer={explainers.byId.get('yearsToRetirement')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'retirementGoal', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({
                title: ok ? 'Share link copied' : 'Share link ready',
                description: ok ? shareUrl : `Copy manually: ${shareUrl}`,
                variant: ok ? 'success' : 'warning'
              });
            }}
            onExportCsv={() => {
              const headers = ['Year', 'Age', 'Start', 'Contributions', 'Growth', 'End'];
              const rows = result.yearlyData.map((row) => [
                row.year,
                row.age,
                Math.round(row.startBalance),
                Math.round(row.contributions),
                Math.round(row.growth),
                Math.round(row.endBalance)
              ]);
              exportRowsToCsv(headers, rows, 'retirement-goal-projection.csv');
              toast({ title: 'CSV exported', description: 'retirement-goal-projection.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Retirement Goal Projection',
                subtitle: `Age ${state.currentAge} to ${state.retirementAge}`,
                summaryLeft: [
                  `Retirement goal: ${formatCurrency(state.retirementGoal)}`,
                  `Current savings: ${formatCurrency(state.currentSavings)}`,
                  `Expected return: ${state.expectedReturn.toFixed(1)}%`,
                  `Years to retirement: ${result.yearsToRetirement}`
                ],
                summaryRight: [
                  `Required monthly savings: ${formatCurrency(result.requiredMonthlySavings)}`,
                  `Projected from current: ${formatCurrency(result.projectedFromCurrent)}`,
                  `Shortfall: ${formatCurrency(Math.abs(result.shortfall))}`,
                  `Already on track: ${result.alreadyOnTrack ? 'Yes' : 'No'}`
                ],
                headers: ['Year', 'Age', 'Start', 'Contributions', 'Growth', 'End'],
                rows: result.yearlyData.map((row) => [
                  row.year,
                  row.age,
                  formatCurrency(row.startBalance),
                  formatCurrency(row.contributions),
                  formatCurrency(row.growth),
                  formatCurrency(row.endBalance)
                ]),
                filename: 'retirement-goal-projection.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'retirement-goal-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Goal progress over time"
          description="Projected balance, annual contributions, and goal line across the full timeline."
          legend={<ChartLegendPills config={chartConfig} keys={['balance', 'contributions', 'goalLine']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="age" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={70}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="balance" type="natural" stroke="var(--color-balance)" strokeWidth={3.1} dot={false} />
              <Line dataKey="contributions" type="natural" stroke="var(--color-contributions)" strokeWidth={1.9} dot={false} />
              <Line dataKey="goalLine" type="natural" stroke="var(--color-goalLine)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Milestone timing" description="When key percentages of your goal are reached.">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Milestone</TableHead>
                <TableHead numeric>Target amount</TableHead>
                <TableHead numeric>Year reached</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.milestones.map((milestone) => (
                <TableRow key={milestone.percent}>
                  <TableCell>{milestone.percent}%</TableCell>
                  <TableCell numeric>{formatCurrency(milestone.amount)}</TableCell>
                  <TableCell numeric>Year {milestone.year}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <ChartCard title="Projection table" description="Full year-by-year balances, contributions, and growth.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Year</TableHead>
                <TableHead numeric>Age</TableHead>
                <TableHead numeric>Start</TableHead>
                <TableHead numeric>Contributions</TableHead>
                <TableHead numeric>Growth</TableHead>
                <TableHead numeric>End</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.yearlyData.map((row) => (
                <TableRow key={`${row.year}-${row.age}`}>
                  <TableCell numeric>{row.year}</TableCell>
                  <TableCell numeric>{row.age}</TableCell>
                  <TableCell numeric>{formatCurrency(row.startBalance)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.contributions)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.growth)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.endBalance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
